'use client';

import {
  type RefObject,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { flushSync } from 'react-dom';

import { useTranslation } from 'react-i18next';

import { formatLocaleDate } from '@kit/shared/utils';
import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import {
  getBookingScrollContainer,
  scrollToBookingElement,
} from '../_lib/booking-scroll-container';
import {
  type AgendaDay,
  agendaScrollPosition,
  agendaStripDates,
  groupClassesByDay,
  parseDateKey,
  toDateKey,
} from '../_lib/class-agenda';
import {
  type ClassEligibility,
  EMPTY_CLASS_FILTERS,
  countActiveClassFilters,
} from '../_lib/class-filters';
import { useFixedHeaderBottom } from '../_lib/hooks/use-fixed-header-bottom';
import type { AvailableClassData } from '../_lib/server/server-actions';
import { ClassCard } from './class-card';
import { ClassFilters, NoMatchingClasses } from './class-filters';
import {
  ClassListSkeleton,
  NoClassesAvailable,
  useFilteredClasses,
} from './class-selection';
import { DateStrip } from './date-strip';

const DAYS_PER_PAGE = 7;
const JUMP_SETTLE_MS = 700;

interface ClassAgendaProps {
  /** Every loaded class across all days, not just one day's. */
  classes: AvailableClassData[];
  selectedClass: string;
  onClassSelect: (
    bookingId: string,
    price: number,
    templateId: string | null,
    isFull?: boolean,
  ) => void;
  isLoading: boolean;
  eligibilityByClassType: Record<string, ClassEligibility[]>;
}

/**
 * Classes for every loaded day in one scrolling list. The sticky date strip
 * follows the day being read and jumps to a day when tapped; it never changes
 * the booking date. Picking a class collapses the list to that class under
 * its date, so the checkout below stays reachable and the booked day is
 * spelled out.
 */
export function ClassAgenda({
  classes,
  selectedClass,
  onClassSelect,
  isLoading,
  eligibilityByClassType,
}: ClassAgendaProps) {
  const { t, i18n } = useTranslation('bookings');
  const { filters, setFilters, filterable, visibleClasses } =
    useFilteredClasses(classes, eligibilityByClassType);
  const [view, setView] = useState({
    browsing: false,
    renderedDays: DAYS_PER_PAGE,
    activeDate: null as string | null,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const stickyTop = useFixedHeaderBottom(rootRef, isLoading);
  const stickyRef = useRef<HTMLDivElement>(null);
  const stripTop = Math.max(0, stickyTop - 1);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const trackingPaused = useRef<number | null>(null);

  const days = useMemo(
    () => groupClassesByDay(visibleClasses),
    [visibleClasses],
  );
  const renderedDays = useMemo(
    () => days.slice(0, view.renderedDays),
    [days, view.renderedDays],
  );
  const stripDates = useMemo(() => agendaStripDates(days), [days]);
  const availableDates = useMemo(
    () => new Set(days.map((day) => day.date)),
    [days],
  );
  const selectedEntry = classes.find((c) => c.booking_id === selectedClass);
  const collapsed = Boolean(selectedEntry) && !view.browsing;
  // The list's root only exists once classes load, so effects that attach to
  // it re-run when it appears (first load, refetch after a sport change).
  const listMounted = !isLoading && days.length > 0;
  const hasMoreDays = view.renderedDays < days.length;
  const stickyHeight = useElementHeight(
    stickyRef,
    !isLoading && classes.length > 0 && !collapsed,
  );

  const selectClass = useCallback(
    (classData: AvailableClassData) => {
      setView((current) => ({ ...current, browsing: false }));
      onClassSelect(
        classData.booking_id,
        classData.price_per_person,
        classData.template_id,
        classData.is_full || classData.virtually_full,
      );
    },
    [onClassSelect],
  );

  const scrollToDay = (date: string) => {
    const section = rootRef.current?.querySelector<HTMLElement>(
      `[data-agenda-day="${date}"]`,
    );
    if (!section) return;
    // A smooth jump passes through other days; keep the tapped day lit
    // (the strip still glides along with the list).
    if (trackingPaused.current) window.clearTimeout(trackingPaused.current);
    trackingPaused.current = window.setTimeout(() => {
      trackingPaused.current = null;
    }, JUMP_SETTLE_MS);
    scrollToBookingElement(
      section,
      stickyTop + (stickyRef.current?.offsetHeight ?? 0),
    );
  };

  const jumpTo = (date: string) => {
    const index = days.findIndex((day) => day.date === date);
    flushSync(() =>
      setView((current) =>
        index < 0
          ? { ...current, browsing: true }
          : {
              ...current,
              browsing: true,
              activeDate: date,
              renderedDays: Math.max(
                current.renderedDays,
                index + DAYS_PER_PAGE,
              ),
            },
      ),
    );
    if (index >= 0) scrollToDay(date);
  };

  // Collapsing removes the days above the pick, so bring the collapsed card
  // into view before the checkout reveal scrolls down to the pay button.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!collapsed || !root) return;
    if (root.getBoundingClientRect().top < stickyTop) {
      const container = getBookingScrollContainer(root);
      const top =
        root.getBoundingClientRect().top +
        (container?.scrollTop ?? window.scrollY) -
        stickyTop -
        8;
      if (container) container.scrollTo({ top, behavior: 'instant' });
      else window.scrollTo({ top, behavior: 'instant' });
    }
  }, [collapsed, stickyTop]);

  // The strip follows the list: every frame it glides between the pills of
  // the day being read and the next one, in step with the scroll, and the
  // highlight switches as each day reaches the strip.
  useEffect(() => {
    const root = rootRef.current;
    if (collapsed || !root) return;
    const target: HTMLElement | Window =
      getBookingScrollContainer(root) ?? window;
    let frame = 0;

    const followStrip = (from?: string, to?: string, progress = 0) => {
      const track =
        stickyRef.current?.querySelector<HTMLElement>('[data-date-track]');
      if (!track) return;
      const centerOf = (date?: string) => {
        const rect = date
          ? track
              .querySelector<HTMLElement>(`[data-date="${date}"]`)
              ?.getBoundingClientRect()
          : undefined;
        return rect ? rect.left + rect.width / 2 : undefined;
      };
      const start = centerOf(from);
      if (start === undefined) return;
      const end = centerOf(to) ?? start;
      const trackRect = track.getBoundingClientRect();
      const delta =
        start +
        (end - start) * progress -
        (trackRect.left + trackRect.width / 2);
      if (Math.abs(delta) >= 0.5) {
        track.scrollBy({ left: delta, behavior: 'instant' });
      }
    };

    const update = () => {
      frame = 0;
      const line = (stickyRef.current?.getBoundingClientRect().bottom ?? 0) + 1;
      const sections = Array.from(
        root.querySelectorAll<HTMLElement>('[data-agenda-day]'),
        (section) => ({
          date: section.dataset.agendaDay ?? '',
          top: section.getBoundingClientRect().top,
        }),
      );
      const { date, index, progress } = agendaScrollPosition(sections, line);
      followStrip(date, sections[index + 1]?.date, progress);
      if (date && !trackingPaused.current) {
        setView((current) =>
          current.activeDate === date
            ? current
            : { ...current, activeDate: date },
        );
      }
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    target.addEventListener('scroll', onScroll, { passive: true });
    frame = requestAnimationFrame(update);
    return () => {
      target.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [collapsed, listMounted]);

  // Render more days as the end of the list comes near.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (collapsed || !hasMoreDays || !sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setView((current) => ({
          ...current,
          renderedDays: current.renderedDays + DAYS_PER_PAGE,
        }));
      },
      {
        root: getBookingScrollContainer(sentinel),
        rootMargin: '0px 0px 800px 0px',
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [collapsed, hasMoreDays, listMounted, view.renderedDays]);

  if (isLoading) return <ClassListSkeleton />;

  if (classes.length === 0) {
    return <NoClassesAvailable message={t('noClassesAvailable')} />;
  }

  if (collapsed && selectedEntry) {
    return (
      <div ref={rootRef} data-test="class-agenda-picked">
        <DayHeading
          date={selectedEntry.booking_date}
          language={i18n.language}
          prefix={t('classAgenda.yourClass')}
          first
        />
        <ClassCard
          classData={selectedEntry}
          isSelected
          onSelect={() => selectClass(selectedEntry)}
          selectedDate={parseDateKey(selectedEntry.booking_date)}
        />
        <Button
          type="button"
          variant="outline"
          className="mt-3 w-full"
          aria-label={t('classAgenda.changeLabel')}
          data-test="class-agenda-change"
          onClick={() => jumpTo(selectedEntry.booking_date)}
        >
          {t('classAgenda.change')}
        </Button>
      </div>
    );
  }

  const activeDate = days.some((day) => day.date === view.activeDate)
    ? view.activeDate
    : days[0]?.date;
  const lastDate = days.at(-1)?.date;

  return (
    <div ref={rootRef} data-test="class-agenda">
      <div
        ref={stickyRef}
        className="bg-card sticky z-10 -mx-2 px-2 pt-1 pb-2"
        // Tucked 1px under the header so a sub-pixel gap never shows.
        style={{ top: stripTop }}
      >
        {days.length > 0 && activeDate && (
          <DateStrip
            selected={parseDateKey(activeDate)}
            onSelect={(date) => jumpTo(toDateKey(date))}
            dates={stripDates}
            language={i18n.language}
            availableDates={availableDates}
            markedDate={
              selectedEntry
                ? parseDateKey(selectedEntry.booking_date)
                : undefined
            }
            centerSelected={false}
          />
        )}
        <div className="pt-2 empty:hidden">
          <ClassFilters
            classes={filterable}
            value={filters}
            onChange={setFilters}
          />
        </div>
      </div>

      {days.length === 0 && countActiveClassFilters(filters) > 0 && (
        <NoMatchingClasses onClear={() => setFilters(EMPTY_CLASS_FILTERS)} />
      )}

      <AgendaDays
        days={renderedDays}
        // Each day's heading tucks 1px under the strip and filters, like the
        // strip under the header.
        headingTop={stripTop + Math.max(0, stickyHeight - 1)}
        selectedClass={selectedClass}
        language={i18n.language}
        onSelect={selectClass}
      />

      {hasMoreDays ? (
        <div ref={sentinelRef} className="h-px" aria-hidden />
      ) : (
        lastDate && (
          <p className="text-muted-foreground py-6 text-center text-xs">
            {t('classAgenda.endOfSchedule', {
              date: formatLocaleDate(parseDateKey(lastDate), i18n.language, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: undefined,
              }),
            })}
          </p>
        )
      )}
    </div>
  );
}

/**
 * The rendered days, memoized so the strip's highlight changing while the
 * customer scrolls doesn't re-render every class card. Each day's heading
 * sticks under the strip and filters until the next day's heading pushes it off.
 */
const AgendaDays = memo(function AgendaDays({
  days,
  headingTop,
  selectedClass,
  language,
  onSelect,
}: {
  days: AgendaDay[];
  headingTop: number;
  selectedClass: string;
  language: string;
  onSelect: (classData: AvailableClassData) => void;
}) {
  return days.map((day, index) => (
    <section
      key={day.date}
      data-agenda-day={day.date}
      className={cn(index > 0 && 'mt-4')}
    >
      <DayHeading
        date={day.date}
        language={language}
        first={index === 0}
        stickyTop={headingTop}
      />
      <div className="-m-1 space-y-2 p-1">
        {day.classes.map((classData) => (
          <ClassCard
            key={classData.booking_id}
            classData={classData}
            isSelected={selectedClass === classData.booking_id}
            onSelect={() => onSelect(classData)}
            selectedDate={parseDateKey(classData.booking_date)}
          />
        ))}
      </div>
    </section>
  ));
});

function DayHeading({
  date,
  language,
  prefix,
  first = false,
  stickyTop,
}: {
  date: string;
  language: string;
  prefix?: string;
  /** Directly under the filters or title, so no gap from a previous day. */
  first?: boolean;
  /** Sticks this far from the top of the scroll area while its day is read. */
  stickyTop?: number;
}) {
  const { t } = useTranslation('bookings');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const relative =
    date === toDateKey(today)
      ? t('classAgenda.today')
      : date === toDateKey(tomorrow)
        ? t('classAgenda.tomorrow')
        : null;

  return (
    <div
      className={cn(
        'px-1 pb-2',
        first ? 'pt-1' : 'pt-2',
        stickyTop !== undefined && 'bg-card sticky z-[5] -mx-2 px-3',
      )}
      style={stickyTop !== undefined ? { top: stickyTop } : undefined}
      data-test="class-agenda-day"
    >
      {prefix && (
        <p className="text-muted-foreground mb-0.5 text-xs font-medium tracking-wide uppercase">
          {prefix}
        </p>
      )}
      <h4 className="text-foreground flex flex-wrap items-center gap-2 text-lg leading-tight font-semibold">
        {formatLocaleDate(parseDateKey(date), language, {
          weekday: 'long',
          day: 'numeric',
          month: 'short',
          year: undefined,
        })}
        {relative && (
          <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs font-medium">
            {relative}
          </span>
        )}
      </h4>
    </div>
  );
}

/** An element's height, tracked as it resizes (e.g. fonts loading in). */
function useElementHeight(
  ref: RefObject<HTMLElement | null>,
  mounted: boolean,
) {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!mounted || !element) return;
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, mounted]);

  return height;
}

const PHONE_QUERY = '(max-width: 767px)';

/** Phone-width web, where the booking form shows the date strip (below `md`). */
export function useIsPhoneWidth() {
  return useSyncExternalStore(
    (onChange) => {
      const query = window.matchMedia(PHONE_QUERY);
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    },
    () => window.matchMedia(PHONE_QUERY).matches,
    () => false,
  );
}
